import debug from "debug";
import { combineLatest, ReplaySubject, Subject } from "rxjs";
import { filter, map, takeUntil, tap } from "rxjs/operators";
import { mapStreamToCanvas, safeCaptureStream } from "../utils";
import { ExtendedMediaStream } from "./ExtendedMediaStream";
import { PlainExtendedMediaStream } from "./PlainExtendedMediaStream";

export class CanvasedExtendedMediaStream implements ExtendedMediaStream {
  private readonly _canvasedStream: ExtendedMediaStream;
  private readonly _canvas: HTMLCanvasElement;
  private readonly _destroy$ = new Subject<void>();
  private readonly _loaded$ = new ReplaySubject<void>(1);
  private readonly _debug: debug.Debugger;

  constructor(
    private readonly _origin: ExtendedMediaStream,
    private readonly _options: Options,
    private readonly _videoElementFactory: () => HTMLVideoElement,
    private readonly _canvasElementFactory: () => HTMLCanvasElement
  ) {
    this._debug = debug("youm-lib:CanvasedExtendedMediaStream");

    this._canvas = this._canvasElementFactory();

    // Canvased stream always has a video track from a canvas.
    // origin.video$ and origin.videoTracks are the way to undestand if it's backed by some real video track.
    this._canvasedStream = new PlainExtendedMediaStream(
      safeCaptureStream(this._canvas)
    );

    // Canvased stream should be in sync with the origin.
    // Remove video tracks from the canvased stream if the origin does not have them.
    if (!this._origin.videoTracks.length) {
      this._canvasedStream.videoTracks.forEach((_) => {
        this._canvasedStream.removeTrack(_);
        _.stop();
      });
    }

    this._origin.audioTracks.forEach((_) => this._canvasedStream.addTrack(_));

    this._origin.addTrack$
      .pipe(
        filter((_) => _.kind === "audio"),
        tap((_) => this._canvasedStream.addTrack(_)),
        takeUntil(this._destroy$)
      )
      .subscribe();

    this._origin.removeTrack$
      .pipe(
        filter((_) => _.kind === "audio"),
        tap((_) => this._canvasedStream.removeTrack(_)),
        takeUntil(this._destroy$)
      )
      .subscribe();

    this._origin.addTrack$
      .pipe(
        filter((_) => _.kind === "video"),
        // Canvased stream may contain 0 or more video tracks (e.g. video track was previously removed).
        tap(() => {
          // Discard all existed video tracks.
          this._canvasedStream.videoTracks.forEach((_) => {
            this._canvasedStream.removeTrack(_);
            _.stop();
          });
        }),
        map(() => this.fetchCanvasMediaStreamVideoTrack()),
        // Add copy of the new track.
        tap((_) => this._canvasedStream.addTrack(_)),
        takeUntil(this._destroy$)
      )
      .subscribe();

    mapStreamToCanvas(this._origin, this._canvas, this._videoElementFactory, {
      toFlip: this._options.toFlip
    })
      .pipe(
        tap(() => this._loaded$.next()),
        takeUntil(this._destroy$)
      )
      .subscribe();
  }

  get stream() {
    return this._canvasedStream.stream;
  }

  get tracks() {
    return this._canvasedStream.tracks;
  }

  get videoTracks() {
    return this._origin.videoTracks.length
      ? this._canvasedStream.videoTracks
      : [];
  }

  get audioTracks() {
    return this._canvasedStream.audioTracks;
  }

  get videoTracks$() {
    return combineLatest([
      this._origin.videoTracks$,
      this._canvasedStream.videoTracks$
    ]).pipe(map((_) => (_[0].length > 0 ? _[1] : [])));
  }

  get audioTracks$() {
    return this._canvasedStream.audioTracks$;
  }

  get addTrack$() {
    return this._canvasedStream.addTrack$;
  }

  get removeTrack$() {
    return this._canvasedStream.removeTrack$;
  }

  get videoDevicesIds() {
    return this._canvasedStream.videoTracks
      .map((_) => _.getSettings().deviceId)
      .filter((_): _ is string => !!_);
  }

  get audioDevicesIds() {
    return this._canvasedStream.audioTracks
      .map((_) => _.getSettings().deviceId)
      .filter((_): _ is string => !!_);
  }

  get loaded$() {
    return this._loaded$.asObservable();
  }

  get isAudioEnabled() {
    return this.audioTracks.every((_) => _.enabled);
  }

  get isAudioEnabled$() {
    return this.audioTracks$.pipe(map((_) => _.every((_) => _.enabled)));
  }

  get isVideoEnabled() {
    return this.videoTracks.every((_) => _.enabled);
  }

  get isVideoEnabled$() {
    return this.videoTracks$.pipe(map((_) => _.every((_) => _.enabled)));
  }

  destroy() {
    const debug = this._debug.extend("destroy");

    debug("run destroy");

    this._destroy$.next();
    this._destroy$.complete();

    this._loaded$.complete();

    this._canvasedStream.destroy();
    this._origin.destroy();
  }

  addTrack(track: MediaStreamTrack) {
    this._origin.addTrack(track);
  }

  removeTrack(track: MediaStreamTrack) {
    this._canvasedStream.removeTrack(track);

    const originTracks =
      track.kind === "audio"
        ? this._origin.audioTracks
        : this._origin.videoTracks;
    originTracks.forEach((_) => {
      this._origin.removeTrack(_);
      _.stop();
    });
  }

  clone() {
    return this._canvasedStream.clone();
  }

  audioEnable() {
    this._canvasedStream.audioEnable();
  }

  audioDisable() {
    this._canvasedStream.audioDisable();
  }

  videoEnable() {
    this._canvasedStream.videoEnable();
  }

  videoDisable() {
    this._canvasedStream.videoDisable();
  }

  private fetchCanvasMediaStreamVideoTrack() {
    const canvasedMediaStream = safeCaptureStream(this._canvas);
    const video = canvasedMediaStream.getVideoTracks()[0]!.clone();

    canvasedMediaStream.getTracks().forEach((_) => {
      canvasedMediaStream.removeTrack(_);
      _.stop();
    });

    return video;
  }
}

interface Options {
  toFlip: boolean;
}
